import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Mail, Phone, User } from 'lucide-react';

const BasicInfoStep = ({ 
  formData, 
  updateFormData, 
  onNext, 
  onPrev, 
  onSkip,
  selectedAvatar
}) => {
  const [name, setName] = useState(formData.basicInfo.name || '');
  const [email, setEmail] = useState(formData.basicInfo.email || '');
  const [phone, setPhone] = useState(formData.basicInfo.phone || '');
  const [location, setLocation] = useState(formData.basicInfo.location || '');
  const [profilePicture, setProfilePicture] = useState(formData.basicInfo.profilePicture || null);
  const [errors, setErrors] = useState({});
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  // Sample location suggestions (in a real app, this would come from an API)
  const sampleLocations = [
    'Mumbai, Maharashtra, India',
    'Delhi, India',
    'Bangalore, Karnataka, India',
    'Hyderabad, Telangana, India',
    'Chennai, Tamil Nadu, India',
    'Kolkata, West Bengal, India',
    'Pune, Maharashtra, India',
    'Ahmedabad, Gujarat, India',
    'Jaipur, Rajasthan, India',
    'Lucknow, Uttar Pradesh, India'
  ];

  // Check if form is complete
  useEffect(() => {
    if (name && email && phone && location) {
      setIsComplete(true);
    } else {
      setIsComplete(false);
    }
  }, [name, email, phone, location]);

  // Handle location input change
  const handleLocationChange = (e) => {
    const value = e.target.value;
    setLocation(value);
    
    if (value.length > 2) {
      const filtered = sampleLocations.filter(loc => 
        loc.toLowerCase().includes(value.toLowerCase())
      );

      setLocationSuggestions(filtered.slice(0, 5));
      setShowLocationSuggestions(true);
    } else {
      setShowLocationSuggestions(false);
    }
  };

  // Handle location suggestion selection
  const handleSelectLocation = (location) => {
    setLocation(location);
    setShowLocationSuggestions(false);
  };

  // Handle profile picture upload
  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfilePicture(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Phone number should be 10 digits';
    }
    
    if (!location.trim()) {
      newErrors.location = 'Location is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle continue button click
  const handleContinue = () => {
    if (validateForm()) {
      updateFormData('basicInfo', {
        name,
        email,
        phone,
        location,
        profilePicture: profilePicture || selectedAvatar
      });
      
      // Show a success animation before moving to next step
      const button = document.getElementById('continue-button');
      if (button) {
        button.classList.add('bg-green-500');
        button.innerText = 'Great job! ✓';
        
        setTimeout(() => {
          onNext();
        }, 800);
      } else {
        onNext();
      }
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-3xl font-bold text-center mb-2">Tell us about yourself 🌟</h1>
        <p className="text-gray-600 text-center mb-8">
          Let's start with some basic information to help employers get to know you
        </p>

        <div className="flex flex-col md:flex-row gap-6 mb-8">
          {/* Profile picture upload */}
          <div className="md:w-1/3">
            <div className="flex flex-col items-center">
              <div className="relative mb-4">
                <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center border-2 border-yellow-400">
                  {profilePicture ? (
                    <img src={profilePicture} alt="Profile" className="w-full h-full object-cover" />
                  ) : selectedAvatar ? (
                    <img src={selectedAvatar} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <User size={48} className="text-gray-400" />
                  )}
                </div>
                <label htmlFor="profile-picture" className="absolute bottom-0 right-0 bg-yellow-400 rounded-full p-2 cursor-pointer shadow-md">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                  </svg>
                  <input 
                    type="file" 
                    id="profile-picture" 
                    className="hidden" 
                    accept="image/*"
                    onChange={handleProfilePictureChange}
                  />
                </label>
              </div>
              <p className="text-sm text-gray-500 text-center">
                Upload a professional photo or use your avatar
              </p>
            </div>
          </div>

          {/* Form fields */}
          <div className="md:w-2/3">
            <div className="space-y-6">
              {/* Name field */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User size={18} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="name"
                    className={`w-full pl-10 pr-3 py-2 border-b-2 ${errors.name ? 'border-red-500' : 'border-gray-300 focus:border-yellow-400'} focus:outline-none transition-colors`}
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
              </div>

              {/* Email field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail size={18} className="text-gray-400" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    className={`w-full pl-10 pr-3 py-2 border-b-2 ${errors.email ? 'border-red-500' : 'border-gray-300 focus:border-yellow-400'} focus:outline-none transition-colors`}
                    placeholder="john.doe@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
              </div>

              {/* Phone field */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Phone size={18} className="text-gray-400" />
                  </div>
                  <input
                    type="tel"
                    id="phone"
                    className={`w-full pl-10 pr-3 py-2 border-b-2 ${errors.phone ? 'border-red-500' : 'border-gray-300 focus:border-yellow-400'} focus:outline-none transition-colors`}
                    placeholder="9876543210"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>
                {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone}</p>}
              </div>

              {/* Location field */}
              <div>
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                  Current Location
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MapPin size={18} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="location"
                    className={`w-full pl-10 pr-3 py-2 border-b-2 ${errors.location ? 'border-red-500' : 'border-gray-300 focus:border-yellow-400'} focus:outline-none transition-colors`}
                    placeholder="City, State, Country"
                    value={location}
                    onChange={handleLocationChange}
                    onFocus={() => location.length > 2 && setShowLocationSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowLocationSuggestions(false), 200)}
                  />
                  
                  {/* Location suggestions */}
                  {showLocationSuggestions && locationSuggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
                      {locationSuggestions.map((loc, index) => (
                        <div
                          key={index}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                          onClick={() => handleSelectLocation(loc)}
                        >
                          <div className="flex items-center">
                            <MapPin size={16} className="text-gray-400 mr-2" />
                            <span>{loc}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {errors.location && <p className="mt-1 text-sm text-red-500">{errors.location}</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Form completion indicator */}
        {isComplete && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-3 bg-green-50 border border-green-200 rounded-md"
          >
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <p className="text-green-700">Looking good! All required fields are filled.</p>
            </div>
          </motion.div>
        )}

        {/* Navigation buttons */}
        <div className="flex justify-between mt-8">
          <button
            type="button"
            className="px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors"
            onClick={onPrev}
          >
            Back
          </button>
          
          <div className="flex gap-4">
            <button
              type="button"
              className="px-6 py-2 text-gray-500 font-medium hover:text-gray-700 transition-colors"
              onClick={onSkip}
            >
              Skip for now
            </button>
            
            <motion.button
              id="continue-button"
              type="button"
              className="px-8 py-3 bg-black text-white font-bold rounded-md shadow-md hover:bg-gray-800 transition-all"
              onClick={handleContinue}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={!isComplete}
            >
              Continue
            </motion.button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default BasicInfoStep;
